import { useCallback } from 'react';
import { useFeatureGate } from '@statsig/react-bindings';

import { useFeatureGateOverrides } from './FeatureGateOverridesProvider';
import useFeatureGateOverrideManagerEnabled from './useFeatureGateOverrideManagerEnabled';

import { FeatureGates } from 'modules/common/constants/featuregates';

/**
 * Returns a resolver mapping a gate name to its local dev override, or `null` when no
 * override should apply and the caller should fall back to the real Statsig value.
 *
 * Overrides only take effect when BOTH the `EnableFeatureGateOverrideManager` Statsig gate
 * and the local dev toggle are enabled. This is the single source of truth for that guard —
 * both `useStatsigGate` (declarative) and `useCheckGate` (imperative) consume it so the two
 * hooks can never drift. Because it subscribes to the overrides context, consumers re-render
 * when an override is toggled.
 */
export default function useGateOverride() {
    const { overrides } = useFeatureGateOverrides();
    const isFeatureGateOverrideManagerEnabled = useFeatureGateOverrideManagerEnabled();
    const isOverrideManagerGateEnabled = useFeatureGate(FeatureGates.EnableFeatureGateOverrideManager).value;

    return useCallback(
        (gateName: string): boolean | null => {
            const overrideValue = overrides[gateName] ?? null;
            if (overrideValue !== null && isOverrideManagerGateEnabled && isFeatureGateOverrideManagerEnabled) {
                return overrideValue;
            }
            return null;
        },
        [overrides, isOverrideManagerGateEnabled, isFeatureGateOverrideManagerEnabled]
    );
}
